// Client-side scalar transform for the live point fast-path (ADR 44).
//
// The ConnectorLiveHub pushes a point's RAW value as it lands. A binding may
// carry a transform pipeline (resolved server-side by design_render's poll). To
// keep a transformed point live — instead of flashing the raw value between
// polls — we apply the *meaningful scalar steps* (calc / filter) to the pushed
// value here. Series-only steps (resample/reduce/sort/limit/organize/rename)
// degenerate on a single value and are skipped. Mirrors the server's
// calc/filter semantics for one numeric cell.

type Step = Record<string, unknown>;

/** Apply a binding's transform steps to a single live value. */
export function applyScalarTransforms(value: number | null, steps: Step[] | undefined): number | null {
  let v = value;
  for (const s of steps ?? []) {
    if (v == null) break;
    const type = s?.type;
    const expr = typeof s?.expression === 'string' ? (s.expression as string) : '';
    if (type === 'calc' && expr) {
      const r = evalExpr(expr, v);
      v = typeof r === 'number' && Number.isFinite(r) ? r : null;
    } else if (type === 'filter' && expr) {
      const r = evalExpr(expr, v);
      if (!r) v = null; // predicate false (or unevaluable) → drop the value
    }
    // rename / organize / sort / limit / reduce / resample: no-op on a scalar.
  }
  return v;
}

// Evaluate an expression where every {field} token resolves to the scalar value
// (a single live point has only the `value` field). Safe by construction: a
// hand-rolled shunting-yard over a fixed operator set — no eval()/Function.
function evalExpr(expr: string, value: number): number | null {
  const src = expr.replace(/\{[^}]*\}/g, `(${value})`);
  try {
    return evalRpn(toRpn(tokenize(src)));
  } catch {
    return null;
  }
}

type Tok = { t: 'num'; v: number } | { t: 'op'; v: string } | { t: 'lp' } | { t: 'rp' };

function tokenize(s: string): Tok[] {
  const out: Tok[] = [];
  const re = /\s*(\d+\.?\d*|>=|<=|==|!=|&&|\|\||[-+*/()<>])/g;
  let m: RegExpExecArray | null;
  let last = 0;
  let expectVal = true; // at the start / after an operator, a '-' is unary
  while ((m = re.exec(s)) !== null) {
    if (m.index !== last) throw new Error('unexpected char');
    last = re.lastIndex;
    const tok = m[1]!;
    if (/^\d/.test(tok)) { out.push({ t: 'num', v: Number(tok) }); expectVal = false; }
    else if (tok === '(') { out.push({ t: 'lp' }); expectVal = true; }
    else if (tok === ')') { out.push({ t: 'rp' }); expectVal = false; }
    else {
      out.push({ t: 'op', v: tok === '-' && expectVal ? 'u-' : tok });
      expectVal = true;
    }
  }
  if (last !== s.length) throw new Error('trailing');
  return out;
}

const PREC: Record<string, number> = {
  '||': 1, '&&': 2, '==': 3, '!=': 3, '<': 4, '>': 4, '<=': 4, '>=': 4,
  '+': 5, '-': 5, '*': 6, '/': 6, 'u-': 7,
};

function toRpn(toks: Tok[]): Tok[] {
  const out: Tok[] = [];
  const ops: Tok[] = [];
  for (const t of toks) {
    if (t.t === 'num') out.push(t);
    else if (t.t === 'op') {
      while (ops.length) {
        const top = ops[ops.length - 1]!;
        if (top.t === 'op' && t.v !== 'u-' && PREC[top.v]! >= PREC[t.v]!) out.push(ops.pop()!);
        else break;
      }
      ops.push(t);
    } else if (t.t === 'lp') ops.push(t);
    else {
      while (ops.length && ops[ops.length - 1]!.t !== 'lp') out.push(ops.pop()!);
      if (!ops.length) throw new Error('paren');
      ops.pop();
    }
  }
  while (ops.length) {
    const o = ops.pop()!;
    if (o.t === 'lp') throw new Error('paren');
    out.push(o);
  }
  return out;
}

function evalRpn(rpn: Tok[]): number {
  const st: number[] = [];
  for (const t of rpn) {
    if (t.t === 'num') { st.push(t.v); continue; }
    if (t.t !== 'op') throw new Error('rpn');
    if (t.v === 'u-') { st.push(-(st.pop() ?? NaN)); continue; }
    const b = st.pop();
    const a = st.pop();
    if (a === undefined || b === undefined) throw new Error('rpn');
    switch (t.v) {
      case '+': st.push(a + b); break;
      case '-': st.push(a - b); break;
      case '*': st.push(a * b); break;
      case '/': st.push(a / b); break;
      case '<': st.push(a < b ? 1 : 0); break;
      case '>': st.push(a > b ? 1 : 0); break;
      case '<=': st.push(a <= b ? 1 : 0); break;
      case '>=': st.push(a >= b ? 1 : 0); break;
      case '==': st.push(a === b ? 1 : 0); break;
      case '!=': st.push(a !== b ? 1 : 0); break;
      case '&&': st.push(a && b ? 1 : 0); break;
      case '||': st.push(a || b ? 1 : 0); break;
      default: throw new Error('op');
    }
  }
  if (st.length !== 1) throw new Error('rpn');
  return st[0]!;
}
